import React, { useState, useEffect } from 'react';
import settingsService from '../../services/settingsService';
import { useSettings } from '../../contexts/SettingsContext';
import '../../styles/ParametresSysteme.css';

interface AppSettings {
  nom: string;
  version: string;
  description: string;
  email: string;
  telephone: string;
  adresse: string;
  logo: string;
  cachet?: string;
}

const ParametresSystemeTab: React.FC = () => {
  const { settings: contextSettings, updateSettings } = useSettings();
  const [settings, setSettings] = useState<AppSettings>(contextSettings);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // Synchroniser avec le contexte
  useEffect(() => {
    setSettings(contextSettings);
    setLogoPreview(settingsService.getLogoUrl());
  }, [contextSettings]);

  // Charger les paramètres actuels
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await settingsService.getSettings();
      if (response.success) {
        setSettings(response.settings);
        setLogoPreview(settingsService.getLogoUrl(true));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      // Utiliser les paramètres par défaut si l'API n'est pas disponible
      const localSettings = settingsService.loadSettingsLocally();
      if (localSettings) {
        setSettings(localSettings);
        setLogoPreview(settingsService.getLogoUrl(true));
      } else {
        setLogoPreview(settingsService.getLogoUrl());
      }
    }
  };

  const handleInputChange = (field: keyof AppSettings, value: string) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    // Mettre à jour le contexte en temps réel pour voir les changements dans l'interface
    updateSettings(newSettings);
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Valider le fichier
    const validationErrors = settingsService.validateLogoFile(file);
    if (validationErrors.length > 0) {
      setMessage({ type: 'error', text: validationErrors[0] });
      return;
    }

    try {
      setIsLoading(true);
      const response = await settingsService.uploadLogo(file);

      if (response.success) {
        const logoUrl = response.logoUrl;
        const newSettings = { ...settings, logo: logoUrl };
        setSettings(newSettings);
        // Utiliser immédiatement l'URL renvoyée par le serveur : ne pas relire l'ancien logo du stockage local.
        setLogoPreview(settingsService.getLogoUrl(true, logoUrl));
        updateSettings(newSettings);
        window.dispatchEvent(new Event('tenant-settings-refresh'));
        setMessage({ type: 'success', text: 'Logo mis à jour avec succès' });
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload du logo:', error);
      
      // Simulation pour le développement - créer un aperçu local
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setLogoPreview(result);
        const newSettings = { ...settings, logo: result };
        setSettings(newSettings);
        updateSettings(newSettings); // Mettre à jour le contexte
        setMessage({ type: 'success', text: 'Logo mis à jour localement (développement)' });
      };
      reader.readAsDataURL(file);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      // Valider les paramètres
      const validationErrors = settingsService.validateSettings(settings);
      if (validationErrors.length > 0) {
        setMessage({ type: 'error', text: validationErrors[0] });
        return;
      }

      setIsLoading(true);
      const cleanedSettings = settingsService.cleanSettings(settings);
      const response = await settingsService.updateSettings(cleanedSettings);
      
      if (response.success) {
        const savedSettings = response.settings || cleanedSettings;
        setSettings(savedSettings);
        setLogoPreview(settingsService.getLogoUrl(true, savedSettings.logo));
        updateSettings(savedSettings);
        window.dispatchEvent(new Event('tenant-settings-refresh'));
        setMessage({ type: 'success', text: 'Paramètres sauvegardés avec succès' });
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      
      // Simulation pour le développement
      const cleanedSettings = settingsService.cleanSettings(settings);
      const saved = settingsService.saveSettingsLocally(cleanedSettings);
      if (saved) {
        updateSettings(cleanedSettings); // Mettre à jour le contexte
        setMessage({ type: 'success', text: 'Paramètres sauvegardés localement (développement)' });
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde des paramètres' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetLogo = () => {
    const defaultLogo = '/assets/logo.png';
    const newSettings = { ...settings, logo: defaultLogo };
    setSettings(newSettings);
    setLogoPreview(settingsService.getLogoUrl(true));
    updateSettings(newSettings); // Mettre à jour le contexte
    setMessage({ type: 'success', text: 'Logo réinitialisé' });
  };

  // 🔴 NOUVEAU : Fonction pour forcer la synchronisation
  const handleForceSync = () => {
    setIsLoading(true);
    
    // Forcer la mise à jour du contexte
    updateSettings(settings);
    
    // Déclencher un événement pour les autres onglets
    localStorage.setItem('force-sync-' + Date.now(), JSON.stringify(settings));
    
    // 🔴 NOUVEAU : Vider le cache des logos
    settingsService.clearLogoCache();
    
    setMessage({ type: 'success', text: 'Synchronisation forcée - Tous les utilisateurs verront les nouveaux paramètres immédiatement' });
    
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  // Fermer le message après 5 secondes
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="parametres-systeme-container">
      <h3>⚙️ Paramètres Système</h3>
      
      {/* Message de feedback */}
      {message && (
        <div className={`message ${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-triangle'}`}></i>
          {message.text}
        </div>
      )}

      <div className="settings-grid">
        {/* Section Logo */}
        <div className="settings-section">
          <h4>🎨 Gestion du Logo</h4>
          <div className="logo-management">
            <div className="logo-preview">
              <img 
                src={logoPreview || settingsService.getImageUrl('/assets/logo.png')} 
                alt="Logo de l'application" 
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = settingsService.getImageUrl('/assets/logo.png');
                }}
              />
            </div>
            <div className="logo-controls">
              <label className="upload-btn">
                <i className="fas fa-upload"></i>
                Télécharger un nouveau logo
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleLogoUpload}
                  style={{ display: 'none' }}
                />
              </label>
              <button 
                className="reset-btn"
                onClick={handleResetLogo}
                type="button"
              >
                <i className="fas fa-undo"></i>
                Réinitialiser
              </button>
            </div>
            <p className="logo-info">
              Formats acceptés: PNG, JPG, SVG • Taille max: 2MB • Résolution recommandée: 200x200px
            </p>
          </div>
        </div>

        {/* Section Informations de l'Application */}
        <div className="settings-section">
          <h4>📋 Informations de l'Application</h4>
          <div className="form-grid">
            <div className="form-group">
              <label>Nom de l'application</label>
              <input
                type="text"
                value={settings.nom}
                onChange={(e) => handleInputChange('nom', e.target.value)}
                placeholder="Nom de l'application"
              />
            </div>
            
            <div className="form-group">
              <label>Version</label>
              <input
                type="text"
                value={settings.version}
                onChange={(e) => handleInputChange('version', e.target.value)}
                placeholder="Version"
              />
            </div>
            
            <div className="form-group full-width">
              <label>Description</label>
              <textarea
                value={settings.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Description de l'application"
                rows={3}
              />
            </div>
            
            <div className="form-group">
              <label>Email de contact</label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Email de contact"
              />
            </div>
            
            <div className="form-group">
              <label>Téléphone</label>
              <input
                type="tel"
                value={settings.telephone}
                onChange={(e) => handleInputChange('telephone', e.target.value)}
                placeholder="Téléphone"
              />
            </div>
            
            <div className="form-group full-width">
              <label>Adresse</label>
              <textarea
                value={settings.adresse}
                onChange={(e) => handleInputChange('adresse', e.target.value)}
                placeholder="Adresse complète"
                rows={2}
              />
            </div>
          </div>
        </div>

        {/* Section Aperçu */}
        <div className="settings-section">
          <h4>👁️ Aperçu</h4>
          <div className="preview-container">
            <div className="app-preview">
              <div className="preview-header">
                <img 
                  src={logoPreview || settingsService.getImageUrl('/assets/logo.png')} 
                  alt="Logo" 
                  className="preview-logo"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = settingsService.getImageUrl('/assets/logo.png');
                  }}
                />
                <h1 className="preview-title">{settings.nom}</h1>
              </div>
              <div className="preview-info">
                <p><strong>Version:</strong> {settings.version}</p>
                <p><strong>Description:</strong> {settings.description}</p>
                <p><strong>Contact:</strong> {settings.email}</p>
                <p><strong>Téléphone:</strong> {settings.telephone}</p>
                <p><strong>Adresse:</strong> {settings.adresse}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="actions-container">
        <button 
          className="btn-save"
          onClick={handleSaveSettings}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <i className="fas fa-spinner fa-spin"></i>
              Sauvegarde...
            </>
          ) : (
            <>
              <i className="fas fa-save"></i>
              Sauvegarder les paramètres
            </>
          )}
        </button>

        <button 
          className="btn-sync"
          onClick={handleForceSync}
          disabled={isLoading}
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '0.875rem 2rem',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '1rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          title="Forcer tous les utilisateurs à voir les nouveaux paramètres immédiatement"
        >
          <i className="fas fa-sync-alt"></i>
          Forcer la synchronisation
        </button>
        
        <button 
          className="btn-reset"
          onClick={loadSettings}
          disabled={isLoading}
        >
          <i className="fas fa-undo"></i>
          Annuler les modifications
        </button>
      </div>
    </div>
  );
};

export default ParametresSystemeTab;
